import { queryClient } from '@qse/api'
import { AppRoutesList } from '@qse/constants'
import { storage } from '@qse/utils'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Simple cross-tab logout synchronization.
 *
 * When a user logs out in one tab, this hook detects it via Storage Event API
 * and triggers an API call in other tabs. That API call will fail (401/403)
 * since the user is logged out, which naturally triggers the logout flow.
 *
 * This approach is clean and doesn't require modifying axios interceptors.
 */
export const useCrossTabLogout = () => {
  const navigate = useNavigate()
  const isHandlingLogout = useRef(false)

  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      // Prevent handling the same logout multiple times
      if (isHandlingLogout.current) {
        return
      }

      // Check if this is a logout event from another tab
      if (event.key === 'logout_broadcast' && event.newValue === 'true') {
        console.log('🔄 Cross-tab logout detected - triggering verification API call')
        isHandlingLogout.current = true

        try {
          // Make a simple API call that will fail if user is logged out
          // The 401/403 error will naturally trigger the logout flow
          await queryClient.fetchQuery({
            queryKey: ['logout_verification'],
            queryFn: async () => {
              const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/users/me`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include',
              })

              if (!response.ok) {
                console.log('🔄 Logout verification failed - user is logged out')
                // Force logout and redirect
                await forceLogoutAndRedirect()
                throw new Error('User logged out')
              }

              return response.json()
            },
            retry: false,
            staleTime: 0,
          })
        } catch (error) {
          console.log('🔄 Cross-tab logout - API call failed as expected, redirecting to login')
          // API call failed, user is logged out, redirect to login
          await forceLogoutAndRedirect()
        } finally {
          // Reset the flag after a delay
          setTimeout(() => {
            isHandlingLogout.current = false
          }, 1000)
        }
      }
    }

    // Helper function to force logout and redirect
    const forceLogoutAndRedirect = async () => {
      try {
        // Clear all client-side data
        queryClient.cancelQueries()
        queryClient.clear()
        localStorage.clear()
        sessionStorage.clear()

        // Redirect to login
        navigate(AppRoutesList.login, { replace: true })

        // Force page reload to ensure clean state
        setTimeout(() => {
          window.location.href = AppRoutesList.login
        }, 100)
      } catch (error) {
        console.error('Error during forced logout:', error)
        window.location.href = AppRoutesList.login
      }
    }

    // Listen for storage events (fired when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [navigate])
}

/**
 * Broadcast a logout event to other tabs.
 * Call this when initiating a logout in the current tab.
 */
export function broadcastLogout() {
  try {
    console.log('📢 Broadcasting logout to other tabs')

    // Set a flag that other tabs will detect via storage event
    storage.localStorage.setItem('logout_broadcast', 'true')

    // Clear the flag after a brief delay so it can be detected again in future logouts
    setTimeout(() => {
      try {
        storage.localStorage.removeItem('logout_broadcast')
      } catch (error) {
        console.warn('Could not remove logout_broadcast flag:', error)
      }
    }, 500)
  } catch (error) {
    console.error('Error broadcasting logout:', error)
  }
}
